import { strapiService } from "@services";
import LegalPage from "components/pages/LegalPage";
import { Page } from "components/templates/Page";
import client from "../../urql-client";
import { BlogArticle } from "components/blog/BlogArticle";
import { useRouter as usePagesRouter } from "next/router";
import Blank from "../_blank";
import Custom404 from "../404";
import { BlogDataQuery, LegalDataQuery } from "gql";
import { LatestCaseStudy, getPageTranslations } from "utils/server";
import { getBlogMetaData } from "@utils";
import { Logger } from "services/logger";
import { useSearchParams } from "next/navigation";
import { GetStaticPaths } from "next";
import { legalSlugs as pages } from "utils/locales/strapi";

const Content = ({ locale, latestCaseStudy, type, ...props }) => {
  const router = usePagesRouter();
  const searchParams = useSearchParams();

  const { postData, err } = props as {
    postData: LegalDataQuery["legalPages"]["data"][number]["attributes"];
    err?: string | null;
    latestCaseStudy: LatestCaseStudy;
  };

  if (router.isFallback) {
    return <Blank />;
  }

  if (err) {
    return <Custom404 latestCaseStudy={latestCaseStudy} locale={locale} />;
  }

  const isJapanLive = process.env.JAPAN_SITE_LIVE === "true";

  if (type === "legalpage") {
    return (
      <Page
        latestCaseStudy={latestCaseStudy}
        hero={null}
        background="#fefbe7"
        navColor="#fefbe7"
        hideRegionSelector={searchParams.get("navigation") === "false"}
        hideFooter={locale === "jp" && !isJapanLive}
        hideLinks={locale === "jp" && !isJapanLive}
        hidePressBar={locale === "jp" && !isJapanLive}
      >
        <LegalPage postData={postData} locale={locale} />
      </Page>
    );
  }

  return (
    <>
      {getBlogMetaData(
        postData as unknown as BlogDataQuery["blogPosts"]["data"][number]["attributes"],
        locale,
      )}
      <Page hero={null}>
        <BlogArticle
          postData={postData as unknown as BlogDataQuery["blogPosts"]["data"][number]["attributes"]}
          contentType="seo-page"
        />
      </Page>
    </>
  );
};

export const getStaticPaths: GetStaticPaths = async (context) => {
  const paths = pages.map(({ slug, region }) => ({
    params: { slug },
    locale: region,
  }));

  return { paths: [], fallback: true };

  return {
    paths,
    fallback: true,
  };
};

export const getStaticProps = async ({ params: { slug }, locale }) => {
  try {
    let postData = await strapiService.getPage({ slug });

    if (!postData) {
      postData = await strapiService.getLegalPage({ slug, locale });
    }

    if (!postData) {
      Logger.error(`A post cannot be found with slug ${slug} at locale ${locale}`);
      return { notFound: true };
    }

    const genericProps = await strapiService.getGenericProps(locale, {
      hasKeyResources: false,
      hasLatestCaseStudy: true,
    });

    return {
      props: {
        postData,
        type: postData.__typename.toLowerCase(),
        ...genericProps,
        messages: await getPageTranslations(locale, "legal-page"),
      },
      revalidate: 300,
    };
  } catch (err) {
    return { notFound: true };
  }
};

export default Content;
